import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { contaSedute, distribuzionePerCategoria, dominante } from '@leopart/calc';
import { allenamentiEndpoints } from '../../../endpoints/allenamenti';
import {
  ICreateTrainingBlockBody,
  ITrainingBlock,
  IReorderTrainingBlocksBody,
  IUpdateTrainingBlockBody,
} from '../../../models/allenamenti';
import { addDays, mondayOf, toIsoDate, weekRangeLabel } from '../allenamenti.constants';

/**
 * Settimana di lavoro di una squadra: navigazione, blocchi raggruppati per
 * giorno e operazioni di scrittura.
 *
 * La chiave di cache contiene teamId: senza, due squadre aperte una dopo
 * l'altra si mostrerebbero i blocchi a vicenda.
 *
 * `isError`/`error`/`refetch` sono esposti perché un fallimento della GET
 * non deve rendere una griglia vuota indistinguibile da "nessun blocco":
 * chi consuma l'hook deve poter mostrare l'errore e offrire un retry.
 */
export const useAllenamentiWeek = (teamId: string) => {
  const qc = useQueryClient();
  const [weekStart, setWeekStart] = useState<Date>(() => mondayOf(new Date()));

  const from = toIsoDate(weekStart);
  const to = toIsoDate(addDays(weekStart, 6));
  const queryKey = ['allenamenti', teamId, from, to] as const;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => (await allenamentiEndpoints.list(teamId, from, to)).data,
    enabled: !!teamId,
  });

  const blocks: ITrainingBlock[] = useMemo(() => data ?? [], [data]);

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const date = addDays(weekStart, i);
        return { date, iso: toIsoDate(date) };
      }),
    [weekStart],
  );

  /**
   * Blocchi per giorno, ordinati per `order`.
   *
   * L'ordinamento NON si eredita dall'array: il server lo restituisce già
   * ordinato, ma l'aggiornamento ottimistico del riordino riscrive `order` sui
   * singoli blocchi senza spostarli nell'array. Fidandosi della posizione,
   * trascinare una card non cambiava nulla a schermo finché non tornava la
   * risposta — cioè proprio nel momento in cui l'anticipo doveva servire.
   */
  const blocksByDay = useMemo(() => {
    const map = new Map<string, ITrainingBlock[]>();
    for (const day of weekDays) map.set(day.iso, []);
    for (const block of blocks) {
      const list = map.get(block.localDate);
      if (list) list.push(block);
    }
    for (const list of map.values()) list.sort((a, b) => a.order - b.order);
    return map;
  }, [blocks, weekDays]);

  const totalMinutes = useMemo(
    () => blocks.reduce((sum, b) => sum + b.durationMinutes, 0),
    [blocks],
  );

  /**
   * Sedute, non giorni: stessa chiave di @leopart/calc (localDate + slot).
   * Oggi `slot` è sempre null quindi il numero coincide con "giorni
   * distinti con almeno un blocco", ma è la formula che regge anche il
   * giorno del doppio allenamento (R7, design M2/M3) — a differenza del
   * vecchio `new Set(blocks.map(b => b.localDate)).size`, che quel giorno
   * conterebbe un solo "giorno" invece di due sedute.
   */
  const trainingDays = useMemo(() => contaSedute(blocks), [blocks]);

  /**
   * Categoria dominante della settimana (id + percentuale sui minuti totali).
   * Solo il dato grezzo: l'etichetta e il colore restano in WeekBoard, che
   * già risolve categoryId → { labelIt, color } tramite la tassonomia.
   *
   * Stessa formula di /training-blocks/analysis (`distribuzionePerCategoria`
   * + `dominante`, entrambe da @leopart/calc, la stessa `percentuale()` a 1
   * decimale sotto il cofano): un solo produttore per questo numero, non più
   * un "38%" qui (arrotondamento intero locale) e un "37.5%" nel donut della
   * vista Analisi sulla stessa identica settimana (R5, design M2/M3).
   */
  const dominantCategory = useMemo(() => {
    const top = dominante(distribuzionePerCategoria(blocks));
    return top ? { categoryId: top.chiave, percent: top.percentuale } : null;
  }, [blocks]);

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['allenamenti', teamId] });
  }, [qc, teamId]);

  const createMutation = useMutation({
    mutationFn: async (body: ICreateTrainingBlockBody) =>
      (await allenamentiEndpoints.create(body)).data,
    onSuccess: () => {
      invalidate();
      toast.success('Blocco aggiunto');
    },
    onError: () => toast.error('Non è stato possibile salvare il blocco'),
  });

  const updateMutation = useMutation({
    mutationFn: async (vars: { id: string; body: IUpdateTrainingBlockBody }) =>
      (await allenamentiEndpoints.update(vars.id, vars.body)).data,
    /**
     * Optimistic update: serve soprattutto al drag-and-drop (spostare un
     * blocco fra colonne deve sentirsi istantaneo, non aspettare il
     * round-trip), ma copre onestamente ogni PATCH — anche dalla modal.
     * In caso di errore si torna allo stato precedente e parte comunque
     * il toast di errore esistente.
     */
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<ITrainingBlock[]>(queryKey);
      qc.setQueryData<ITrainingBlock[]>(queryKey, (old) =>
        old?.map((b) => (b._id === vars.id ? { ...b, ...vars.body } : b)),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(queryKey, context.previous);
      toast.error('Non è stato possibile aggiornare il blocco');
    },
    onSuccess: () => {
      invalidate();
      toast.success('Blocco aggiornato');
    },
  });

  /**
   * Riordino verticale e spostamento di giorno, in una richiesta sola.
   *
   * L'aggiornamento ottimistico qui non è un lusso: il trascinamento deve
   * concludersi dove il dito ha lasciato la card, non riposizionarla mezzo
   * secondo dopo. Riscrive `localDate` e `order` dei soli blocchi elencati,
   * lasciando gli altri come sono.
   */
  const reorderMutation = useMutation({
    mutationFn: async (body: IReorderTrainingBlocksBody) =>
      (await allenamentiEndpoints.reorder(body)).data,
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<ITrainingBlock[]>(queryKey);

      const position = new Map<string, { localDate: string; order: number }>();
      for (const day of body.days) {
        day.blockIds.forEach((id, order) =>
          position.set(id, { localDate: day.localDate, order }),
        );
      }

      qc.setQueryData<ITrainingBlock[]>(queryKey, (old) =>
        old?.map((b) => {
          const next = position.get(b._id);
          return next ? { ...b, ...next } : b;
        }),
      );
      return { previous };
    },
    onError: (_err, _body, context) => {
      if (context?.previous) qc.setQueryData(queryKey, context.previous);
      toast.error('Non è stato possibile spostare il blocco');
    },
    // Nessun toast di successo: un riordino riuscito si VEDE, e un messaggio
    // per ogni trascinamento sarebbe rumore su un gesto che si ripete.
    onSuccess: () => invalidate(),
  });

  const removeMutation = useMutation({
    mutationFn: async (id: string) => (await allenamentiEndpoints.remove(id)).data,
    onSuccess: () => {
      invalidate();
      toast.success('Blocco eliminato');
    },
    onError: () => toast.error('Non è stato possibile eliminare il blocco'),
  });

  return {
    weekStart,
    weekLabel: weekRangeLabel(weekStart),
    weekDays,
    blocks,
    blocksByDay,
    totalMinutes,
    totalBlocks: blocks.length,
    trainingDays,
    dominantCategory,
    isLoading,
    isError,
    error,
    refetch,
    goPrevWeek: () => setWeekStart((w) => addDays(w, -7)),
    goNextWeek: () => setWeekStart((w) => addDays(w, 7)),
    createBlock: createMutation.mutateAsync,
    updateBlock: updateMutation.mutateAsync,
    reorderBlocks: reorderMutation.mutateAsync,
    removeBlock: removeMutation.mutateAsync,
    isSaving:
      createMutation.isPending ||
      updateMutation.isPending ||
      removeMutation.isPending,
    /**
     * Solo la creazione (F1, hardening double-submit): "Incolla" richiama
     * `createBlock` senza passare dal form, quindi non ha un `isSaving` di
     * modal a cui appoggiarsi per disabilitarsi durante la richiesta — due
     * click ravvicinati altrimenti creano due blocchi con lo stesso `order`.
     */
    isCreatingBlock: createMutation.isPending,
  };
};
